import React, { useMemo } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import Clipboard from "@react-native-clipboard/clipboard";
import TOKENS from "../styles/tokens";

type Props = {
  visible: boolean;
  sourcePaths: string[];
  onClose: () => void;
  onInsert: (path: string) => void;
};

const SourcePathCatalogDrawer: React.FC<Props> = ({
  visible,
  sourcePaths,
  onClose,
  onInsert,
}) => {
  const grouped = useMemo(() => {
    const root: string[] = [];
    const patient: string[] = [];
    const formData: string[] = [];
    const photos: string[] = [];
    const other: string[] = [];

    sourcePaths.forEach((path) => {
      if (path.startsWith("patient_record.")) {
        patient.push(path);
      } else if (path.startsWith("form_data.")) {
        formData.push(path);
      } else if (path.startsWith("photo_urls")) {
        photos.push(path);
      } else if (!path.includes(".")) {
        root.push(path);
      } else {
        other.push(path);
      }
    });

    return [
      { title: "Patient Record", data: patient },
      { title: "Form Data", data: formData },
      { title: "Photo URLs", data: photos },
      { title: "Root", data: root },
      { title: "Other", data: other },
    ].filter((item) => item.data.length > 0);
  }, [sourcePaths]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.backdrop}>
        <View style={styles.drawer}>
          <View style={styles.header}>
            <Text style={styles.title}>Source Path Catalog</Text>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>Close</Text>
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={styles.scrollContent}>
            {grouped.map((group) => (
              <View key={group.title} style={styles.section}>
                <Text style={styles.sectionTitle}>{group.title}</Text>
                {group.data.map((path) => (
                  <View key={path} style={styles.row}>
                    <Text selectable style={styles.pathText}>
                      {path}
                    </Text>
                    <View style={styles.rowActions}>
                      <Pressable
                        style={styles.actionButton}
                        onPress={() => {
                          Clipboard.setString(path);
                        }}
                      >
                        <Text style={styles.actionText}>Copy</Text>
                      </Pressable>
                      <Pressable
                        style={styles.actionButton}
                        onPress={() => onInsert(path)}
                      >
                        <Text style={styles.actionText}>Insert</Text>
                      </Pressable>
                    </View>
                  </View>
                ))}
              </View>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(15,23,42,0.45)",
    justifyContent: "flex-end",
  },
  drawer: {
    backgroundColor: TOKENS.color.surface,
    borderTopLeftRadius: TOKENS.radius.xl,
    borderTopRightRadius: TOKENS.radius.xl,
    maxHeight: "82%",
    paddingHorizontal: TOKENS.spacing.md,
    paddingTop: TOKENS.spacing.md,
    paddingBottom: TOKENS.spacing.lg,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: TOKENS.spacing.sm,
  },
  title: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.lg,
    lineHeight: TOKENS.lineHeight.lg,
    color: TOKENS.color.textPrimary,
  },
  closeButton: {
    minHeight: 44,
    minWidth: 44,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: TOKENS.spacing.sm,
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
  },
  closeButtonText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  scrollContent: {
    gap: TOKENS.spacing.md,
    paddingBottom: TOKENS.spacing.lg,
  },
  section: {
    gap: TOKENS.spacing.xs,
  },
  sectionTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  row: {
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    padding: TOKENS.spacing.sm,
    gap: TOKENS.spacing.xs,
  },
  pathText: {
    fontFamily: TOKENS.font.mono,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
    color: TOKENS.color.textPrimary,
  },
  rowActions: {
    flexDirection: "row",
    gap: TOKENS.spacing.xs,
  },
  actionButton: {
    minHeight: 44,
    paddingHorizontal: TOKENS.spacing.sm,
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    borderColor: TOKENS.color.borderStrong,
    justifyContent: "center",
  },
  actionText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
});

export default SourcePathCatalogDrawer;
